<template>
  <v-dialog
    v-model="show"
    width="70%"
    overlay-color="darktext"
    overlay-opacity="0.6"
    content-class="content-create-template-dialog"
    :persistent="loading ? true : false"
    data-testid="dialog"  
    scrollable
    ref="dialog"
  >
    <v-card
      elevation="0"
      color="card_background_grey"
      class="overflow-x-hidden"
    >
      <AlertMessages
        v-if="alertValue"
        :alert-type="alertType"
        :alert-msg="alertMsg"
        @reset="alertValue = false"
      />
      <v-card-title class="white pb-0">
        <v-row
          no-gutters
          v-if="preview"
        >
          <v-col>
            <v-toolbar
              class="pa-0"
              dense
              flat
              color="white"
            >
              <v-btn
                ref="back"
                text
                data-testid="back-button"
                class="rounded-xxl text-none pa-4"
                :ripple="false"
                style="z-index:1;"
                color="greytext"
                @click="resetScroll(), preview=false"
              >
                <v-icon class="ml-n1">mdi-chevron-left</v-icon>{{ $t('back') }}
              </v-btn>
              <v-spacer />
              <v-toolbar-title>
                <p class="text-h5 font-weight-bold text-center mx-auto mb-0">
                  {{ $t('activityTemplatePreviewTitle') }}
                </p>
              </v-toolbar-title>
              <v-spacer />
              <v-icon
                ref="cancel"
                data-testid="cancel-button"
                min-width="100"
                class="rounded-xxl text-none"
                style="z-index:1;"
                @click="closeDialog"
              >
                mdi-close
              </v-icon>
            </v-toolbar>
            <v-toolbar class="pa-0"
              dense
              flat
              color="white">
            </v-toolbar>
          </v-col>
        </v-row>
        <v-row
          no-gutters
          v-else
        >
          <v-col>
            <v-toolbar
              class="pa-0"
              dense
              flat
              color="white"
            >
              <v-spacer />
              <v-toolbar-title>
                <p class="text-h5 font-weight-bold text-center mx-auto mb-0">
                  {{ $t('chooseActivityTemplateTitle') }}
                </p>
              </v-toolbar-title>
              <v-spacer />
              <v-icon
                ref="cancel"
                data-testid="cancel-button"
                min-width="100"
                class="rounded-xxl text-none"
                style="z-index:1;"
                @click="closeDialog"
              >
                mdi-close
              </v-icon>
              <template v-slot:extension>
                <v-tabs
                  v-model="templateType"
                  centered
                  slider-color="accent"
                  data-testid="template-tabs"
                >
                  <v-tab
                    href="#standard"
                    @click="resetScroll"
                    data-testid="standard-tab"
                  >
                    <span class="text-body-1 darktext--text">{{ $t('standardTemplatesTabTitle') }}</span>
                  </v-tab>
                  <v-tab
                    href="#custom"
                    @click="resetScroll"
                    data-testid="custom-tab"
                  >
                    <span class="text-body-1 darktext--text">{{ $t('customTemplatesTabTitle') }}</span>
                  </v-tab>
                  <v-tab
                    href="#shared"
                    @click="resetScroll"
                    data-testid="shared-tab"
                  >
                    <span class="text-body-1 darktext--text">{{ $t('sharedTemplatesTabTitle') }}</span>
                  </v-tab>
                </v-tabs>
              </template>
            </v-toolbar>
          </v-col>
        </v-row>
      </v-card-title>
      <v-divider class="elevation-2" dark />
      <v-card-text v-if="preview && !loadingTemplate" style="height: 70vh" ref="content">
        <v-row class="my-3">
          <v-col 
            cols="12"
            md="6"
          > 
            <v-img
              max-height="340px"
              class="rounded-xxl mb-4"
              data-testid="template-image"
              :src="(selectedTemplate.image) ? selectedTemplate.image : require('./../assets/background-hub.jpg')"
            /> 
            <p
              class="text-h5 text--primary ml-4 font-weight-bold"
              data-testid="template-title"
            >
              {{ selectedTemplate.title }}
            </p>
            <p
              class="text-body-2 ml-4"
              data-testid="template-author"
            >
              {{ $t('createdBy') }}: {{ selectedTemplate.author }}
            </p>

            <div
              v-if="selectedTemplate.type == 'custom' && spacesTemplateSharedWith.length > 0"
              data-testid="list-spaces-shared-with"
            >
              <p class="text-body-1 text--primary ml-4 mb-2 font-weight-bold">
                {{ $t('shareTemplateListSpaces') }}:
              </p>
              <span
                v-for="(item, index) in spacesTemplateSharedWith"
                :key="index"
                class="d-block text-body-2 ml-4 mb-1"
              >
                {{ item.attributes.name }}
              </span>
            </div>
          </v-col>
          <v-col 
            cols="12"
            md="6"
          >  
            <v-btn
              v-if="(space == 'templates' && selectedTemplate.is_owner == true) || (space != 'templates' && selectedTemplate.is_owner == true && selectedTemplate.type == 'custom')"
              icon
              data-testid="remove-button"
              class="rounded-xxl text-none"
              :ripple="false"
              color="error"
              absolute
              right
              @click.stop="
                dialogVisibleDeleteTemplate = true, 
                confirmationDialogTitle = $t('confirmationDialogTitleDeleteTemplate'), 
                confirmationDialogText = $t('confirmationDialogTextDeleteTemplate'), 
                confirmationDialogSubmitBtn = $t('delete')
                confirmationDialogCancelBtn = $t('no')"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
            <p
              class="text-body-1 text--primary mt-4"
            >
              {{ $t('description') }}:
            </p>
            <p
              class="text-body-1 mt-4"
              data-testid="template-description"
            >
              {{ selectedTemplate.description }}
            </p>
            <div
              v-if="selectedTemplate.feed_items.length > 0"
              class="my-7"
              data-testid="template-feed-items"
            >
              <p class="text-body-1 text--primary">
                {{ $t('listFeedItems') }}:
              </p>
              <span
                v-for="(item, index) in selectedTemplate.feed_items"
                :key="index"
                class="d-block ml-3"
              >
                <v-icon
                  v-if="item.entity_type == 'activity_page' || item.entity_type == 'scorm'"
                  class="mr-3"
                >
                  mdi-text
                </v-icon>
                <v-icon
                  v-if="item.entity_type == 'quiz'"
                  class="mr-3"
                >
                  mdi-chat-question-outline
                </v-icon>
                <v-icon
                  v-if="item.entity_type == 'checklist'"
                  class="mr-3"
                >
                  mdi-order-bool-ascending-variant
                </v-icon>
                <v-icon
                  v-if="item.entity_type == 'survey'"
                  class="mr-3"
                >
                mdi-help-box-multiple-outline
                </v-icon>
                {{ item.title }}
              </span>
            </div>
          </v-col>
        </v-row>
        <v-card-actions style="position:absolute; bottom: 8px; right: 24px;">
          <v-tooltip v-if="selectedTemplate.type == 'custom' && activityContainsScorm" top>
            <template v-slot:activator="{ on }">
              <div v-on="on">
                <v-btn
                  v-if="selectedTemplate.type == 'custom' && activityContainsScorm"
                  disabled
                  ref="share"
                  data-testid="share-button"
                  type="submit"
                  color="accent"
                  min-width="100"
                  elevation="0"
                  class="rounded-xxl text-none px-4 submit mr-2"
                  style="background: white;"
                >
                  {{ $t('share') }}
                </v-btn>
                </div>
              </template>
              {{  $t('shareTemplateWithScormError') }}
          </v-tooltip>
          <v-btn
            v-if="selectedTemplate.type == 'custom' && !activityContainsScorm"
            outlined
            ref="share"
            data-testid="share-button"
            type="submit"
            color="accent"
            min-width="100"
            elevation="0"
            class="rounded-xxl text-none px-4 submit"
            @click.stop="
            dialogVisibleShareTemplate = true"
            style="background: white;"
          >
            {{ $t('share') }}
          </v-btn>
          <v-btn
            ref="preview"
            data-testid="preview-button"
            type="submit"
            color="accent"
            outlined
            min-width="100"
            dark
            elevation="0"
            class="rounded-xxl text-none px-4 submit"
            @click.stop="
            dialogVisiblePreviewActivity = true"
            style="background: white;"
           >
            {{ $t('preview') }}
          </v-btn>
          <v-btn
            ref="submit"
            data-testid="submit-button"
            type="submit"
            color="accent"
            min-width="100"
            dark
            elevation="0"
            class="rounded-xxl text-none px-4 submit"
            :loading="loading"
            @click="createActivity(selectedTemplate.id)"
           >
            {{ $t('useBtn') }}
          </v-btn>
      </v-card-actions>
      </v-card-text>
      <v-card-text v-else style="height: 70vh" ref="content">
        <v-progress-circular
              v-if="loadingData"
              size="72"
              indeterminate
              class="text-center justify-center ma-8 d-block mx-auto"
            />    
            <v-tabs-items v-model="templateType" class="card_background_grey">
            <v-tab-item value="standard" class="my-3" :transition="false" :reverse-transition="false">                                 
              <v-row>
                <v-col 
                  v-if="!loadingData"
                  cols="12"
                  md="6"
                  lg="3"
                >    
                  <v-hover>
                    <template v-slot:default="{ hover }">
                      <v-card
                        data-testid="activity-template-start-from-scratch-card"
                        rounded="xxl"
                        max-width="350"
                        height="150"
                        class="text-center mb-13 mx-auto"
                        @click="createActivity()"
                      >
                        <v-icon
                          x-large
                          class="fill-height"
                        >
                          mdi-plus
                        </v-icon>
                        <v-fade-transition>
                          <v-overlay
                            v-if="hover"
                            absolute
                            color="accent"
                            opacity="0.60"
                          />
                        </v-fade-transition>
                        <v-card-subtitle :class="hover? 'accent--text' : ''">
                          {{ $t('createActivityNoTemplate') }}
                        </v-card-subtitle>
                      </v-card>
                    </template>
                  </v-hover>
                </v-col> 
                <v-col
                  v-for="(template, index) in standardTemplates"
                  :key="index"
                  cols="12"
                  md="6"
                  lg="3"
                >    
                  <v-hover>
                    <v-card
                      slot-scope="{ hover }"
                      data-testid="activity-template-card"
                      rounded="xxl"
                      max-width="350"
                      height="150"
                      elevation="0"
                      class="text-center mb-13 mx-auto"
                      :disabled="loading"
                      @click="previewTemplate(template, 'standard')"
                    >
                      <v-img
                        height="150"
                        :src="template.image ? template.image : trainCoverURL"
                      >
                      <v-card-text
                          v-if="template.is_owner && space == 'templates'"
                          class="py-5"
                        >
                          <v-row
                            data-testid="created-by-the-owner"
                            no-gutters
                            class="mx-auto"
                          >
                            <v-col
                              class="d-flex align-start ml-1"
                              cols="12"
                              sm="12"
                              md="12"
                            >
                              <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                  <v-chip
                                    small
                                    color="warning"
                                    v-on="on"
                                  >
                                    <v-icon
                                      color="darktext"
                                      small
                                    >
                                      mdi-account-cog
                                    </v-icon>
                                  </v-chip>
                                </template>
                                <span>{{ $t('createdByMe') }}</span>
                              </v-tooltip>
                            </v-col>
                          </v-row>
                        </v-card-text>
                      </v-img>
                      <v-card-subtitle :class="hover? 'accent--text' : ''">
                        <v-clamp
                          autoresize
                          :max-lines="2"
                        >
                                        {{ template.title }}
                        </v-clamp>
                      </v-card-subtitle>
                    </v-card>
                  </v-hover>
                </v-col>
              </v-row>      
            </v-tab-item>   
            <v-tab-item value="custom" class="my-3" :transition="false" :reverse-transition="false">
              <v-row
              v-if="customTemplates.length == 0"
              class="fill-height"
              align="center"
            >
              <v-col
                data-testid="empty-custom-template-block"
                class="fill-height mx-auto pt-16"
                cols="12"
                md="6"
                align="center"

              >
                <span class="text-h5 font-weight-bold text-center greytextlight--text" >
                {{ $t('emptyCustomTemplateBlock') }}
                </span>

              </v-col>
            </v-row>
              <v-row v-else>
                <v-col
                  v-for="(template, index) in customTemplates"
                  :key="index"
                  cols="12"
                  md="6"
                  lg="3"
                >    
                  <v-hover>
                    <v-card
                      slot-scope="{ hover }"
                      data-testid="activity-template-card"
                      rounded="xxl"
                      max-width="350"
                      height="150"
                      elevation="0"
                      class="text-center mb-13 mx-auto"
                      :disabled="loading"
                      @click="previewTemplate(template, 'custom')"
                    >
                      <v-img
                        height="150"
                        :src="template.image ? template.image : trainCoverURL"
                      >
                        <v-card-text
                          v-if="template.is_owner"
                          class="py-5"
                        >
                          <v-row
                            data-testid="created-by-the-owner"
                            no-gutters
                            class="mx-auto"
                          >
                            <v-col
                              class="d-flex align-start ml-1"
                              cols="12"
                              sm="12"
                              md="12"
                            >
                              <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                  <v-chip
                                    small
                                    color="warning"
                                    v-on="on"
                                  >
                                    <v-icon
                                      color="darktext"
                                      small
                                    >
                                      mdi-account-cog
                                    </v-icon>
                                  </v-chip>
                                </template>
                                <span>{{ $t('createdByMe') }}</span>
                              </v-tooltip>
                            </v-col>
                          </v-row>
                        </v-card-text>
                      </v-img>
                      <v-card-subtitle :class="hover? 'accent--text' : ''">
                        <v-clamp
                          autoresize
                          :max-lines="2"
                        >
                          {{ template.title }}
                        </v-clamp>
                      </v-card-subtitle>
                    </v-card>
                  </v-hover>
                </v-col>
              </v-row> 
            </v-tab-item>

            <v-tab-item value="shared" class="my-3" :transition="false" :reverse-transition="false">
              <v-row
              v-if="sharedTemplates.length == 0"
              class="fill-height"
              align="center"
            >
              <v-col
                data-testid="empty-shared-template-block"
                class="fill-height mx-auto pt-16"
                cols="12"
                md="6"
                align="center"

              >
                <span class="text-h5 font-weight-bold text-center greytextlight--text" >
                {{ $t('emptySharedTemplateBlock') }}
                </span>

              </v-col>
            </v-row>
              <v-row v-else>
                <v-col
                  v-for="(template, index) in sharedTemplates"
                  :key="index"
                  cols="12"
                  md="6"
                  lg="3"
                >    
                  <v-hover>
                    <v-card
                      slot-scope="{ hover }"
                      data-testid="activity-template-card"
                      rounded="xxl"
                      max-width="350"
                      height="150"
                      elevation="0"
                      class="text-center mb-13 mx-auto"
                      :disabled="loading"
                      @click="previewTemplate(template, 'shared')"
                    >
                      <v-img
                        height="150"
                        :src="template.image ? template.image : trainCoverURL"
                      >
                        <v-card-text
                          v-if="template.is_owner"
                          class="py-5"
                        >
                          <v-row
                            data-testid="created-by-the-owner"
                            no-gutters
                            class="mx-auto"
                          >
                            <v-col
                              class="d-flex align-start ml-1"
                              cols="12"
                              sm="12"
                              md="12"
                            >
                              <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                  <v-chip
                                    small
                                    color="warning"
                                    v-on="on"
                                  >
                                    <v-icon
                                      color="darktext"
                                      small
                                    >
                                      mdi-account-cog
                                    </v-icon>
                                  </v-chip>
                                </template>
                                <span>{{ $t('createdByMe') }}</span>
                              </v-tooltip>
                            </v-col>
                          </v-row>
                        </v-card-text>
                      </v-img>
                      <v-card-subtitle :class="hover? 'accent--text' : ''">
                        <v-clamp
                          autoresize
                          :max-lines="2"
                        >
                          {{ template.title }}
                        </v-clamp>
                      </v-card-subtitle>
                    </v-card>
                  </v-hover>
                </v-col>
              </v-row> 
            </v-tab-item>
        </v-tabs-items>  
      </v-card-text>
    </v-card>

    <ConfirmationDialog
      v-if="dialogVisibleDeleteTemplate"
      ref="confirmationDialogDeleteTemplate"
      :visible="dialogVisibleDeleteTemplate"
      :cd-title="confirmationDialogTitle"
      :cd-text="confirmationDialogText"
      :cd-submit-btn="confirmationDialogSubmitBtn"
      :cd-cancel-btn="confirmationDialogCancelBtn"
      data-testid="confirmation-dialog-delete-template"
      @close="dialogVisibleDeleteTemplate = false"
      @proceedSubmit="deleteTemplate(selectedTemplate.id)"
    />

    <ConfirmationDialogPreviewActivity
      v-if="dialogVisiblePreviewActivity"
      ref="confirmationDialogPreviewActivity"
      :visible="dialogVisiblePreviewActivity"
      :activity-id="selectedTemplate.id"
      :activity-title="selectedTemplate.title"
      data-testid="confirmation-dialog-preview-activity"
      @close="dialogVisiblePreviewActivity = false"
    />

    <ConfirmationDialogShareTemplate
      v-if="dialogVisibleShareTemplate"
      ref="confirmationDialogPreviewActivity"
      :visible="dialogVisibleShareTemplate"
      :activity-id="selectedTemplate.id"
      data-testid="confirmation-dialog-share-template"
      @close="dialogVisibleShareTemplate = false"
      @proceedSubmit="showSuccessMsg()"
    />
  </v-dialog>
</template>
<script>
import i18n from './../plugins/i18n'
import { createActivity, getTemplates, deleteTemplate, getPublicTemplates, getSpacesTemplateSharedWith } from './../api/activityApi'
import { getSharedTemplates } from './../api/spaceApi'

import ConfirmationDialog from './ConfirmationDialog'
import ConfirmationDialogPreviewActivity from './ConfirmationDialogPreviewActivity'
import AlertMessages  from './AlertMessages'
import VClamp from 'vue-clamp'
import ConfirmationDialogShareTemplate from './ConfirmationDialogShareTemplate'

export default {
    components: {
        ConfirmationDialog,
        ConfirmationDialogPreviewActivity,
        AlertMessages,
        VClamp,
        ConfirmationDialogShareTemplate
    },
    props: {
      visible: Boolean
    },

    data(){
        return{
            valid: true,
            alertMsg: '',
            alertType: '',
            alertValue: false,
            trainCoverURL: require('./../assets/background-hub.jpg'),
            spaceId: window.$cookies.get('space_id'),
            space: window.location.host.split('.')[0],
            preview: false,
            loadingData: false,
            loading: false,
            customTemplates: [],
            standardTemplates: [],
            sharedTemplates: [],
            selectedTemplate: null,
            dialogVisibleDeleteTemplate: false,
            confirmationDialogTitle: '',
            confirmationDialogText: '',
            confirmationDialogCancelBtn: '',
            confirmationDialogSubmitBtn: '',
            templateType: 'standard',
            dialogVisiblePreviewActivity: false,
            dialogVisibleShareTemplate: false,
            activityContainsScorm: false,
            spacesTemplateSharedWith: [],
            loadingTemplate: false
        }
    },
    computed: {
    show: {
      get () {
        return this.visible
      },
      set (value) {
        if (!value) {
          this.$emit('close')
        }
         
      }
    }
  },
    async created(){
        this.loadingData = true
        await this.fetchTemplates()
        this.loadingData = false

    },

    methods:{    
        async fetchTemplates(){
            var fetchedTemplates= []
            var fetchedPublicTemplates= []
            var fetchedSharedTemplates= []
            try {
                const response = await getTemplates(this.spaceId);
                if(response.status == 200) {
                  if(response.data.data.length > 0){
                      response.data.data.forEach(function(item) {
                          const obj = {id: item.id, title: item.attributes.title, description: item.attributes.description, image: item.attributes.image,is_owner:item.attributes.is_owner, feed_items: item.attributes.feed_items ? JSON.parse(item.attributes.feed_items) : {}, author: item.meta.user.data.attributes.first_name + ' ' + item.meta.user.data.attributes.last_name }
                          fetchedTemplates.push(obj)
                      })
                  }
                  this.customTemplates = fetchedTemplates

                  const responsePublic = await getPublicTemplates()
                  if(responsePublic.status == 200) {
                    if(responsePublic.data.data.length > 0){
                      responsePublic.data.data.forEach(function(item) {
                          const obj = {id: item.id, title: item.attributes.title, description: item.attributes.description, image: item.attributes.image,is_owner:item.attributes.is_owner, feed_items: item.attributes.feed_items ? JSON.parse(item.attributes.feed_items) : {}, author: item.meta.user.data.attributes.first_name + ' ' + item.meta.user.data.attributes.last_name }
                          fetchedPublicTemplates.push(obj)
                      })
                    }
                    this.standardTemplates = fetchedPublicTemplates
                  }

                  const responseShared = await getSharedTemplates(this.spaceId)
                  if(responseShared.status == 200) {
                    if(responseShared.data.data.length > 0){
                      responseShared.data.data.forEach(function(item) {
                          const obj = {id: item.id, title: item.attributes.title, description: item.attributes.description, image: item.attributes.image,is_owner:item.attributes.is_owner, feed_items: item.attributes.feed_items ? JSON.parse(item.attributes.feed_items) : {}, author: item.meta.user.data.attributes.first_name + ' ' + item.meta.user.data.attributes.last_name }
                          fetchedSharedTemplates.push(obj)
                      })
                    }
                    this.sharedTemplates = fetchedSharedTemplates
                  }
                }                
            } catch (e) {
                if(e.response){
                    this.alertType = 'error'
                    if (e.response.status == 422 ){
                        this.alertMsg =  i18n.t('serverError')
                    } else if (e.response.status == 403){
                        this.$router.push({name: 'PageAccessDenied'})
                    } else {
                        this.alertMsg =  i18n.t('serverError')
                    }
                    this.$emit('proceedSubmit', this.alertMsg, this.alertType) 
                }else{
                    this.alertType = 'error'
                    this.alertMsg =  i18n.t('serverError')
                    this.$emit('proceedSubmit', this.alertMsg, this.alertType)       
                }
            }
        }, 

        async createActivity(id){
            this.loading = true
            try {
                let data = {
                    data: {
                        type: "activities", 
                        attributes: {
                            title: 'Untitled activity',
                            template: id
                        },
                        relationships: {
                            space: {
                                data: {
                                    type: "spaces",
                                    id: "" + this.spaceId + ""
                                }
                            }
                        }
                    }
                };
                const response = await createActivity(data, this.spaceId);
                if(response){
                    if(response.status == 201) {
                        if(response.data.data.attributes.top_feed_entity == "page"){
                          this.$router.push({name: 'ViewActivityPage', params: {id: response.data.data.id, pageId: response.data.data.attributes.activity_page_id, successfullyCreatedActivity: true}})
                        }else if(response.data.data.attributes.top_feed_entity == "quiz"){
                            this.$router.push({name: 'ViewActivityQuiz', params: {id: response.data.data.id, quizId: response.data.data.attributes.activity_page_id, successfullyCreatedActivity: true}})
                        }else if(response.data.data.attributes.top_feed_entity == "scorm"){
                            this.$router.push({name: 'ViewActivityScorm', params: {id: response.data.data.id, scormId: response.data.data.attributes.activity_page_id, successfullyCreatedActivity: true}})
                        }else if(response.data.data.attributes.top_feed_entity == "checklist"){
                            this.$router.push({name: 'ViewActivityChecklist', params: {id: response.data.data.id, checklistId: response.data.data.attributes.activity_page_id, successfullyCreatedActivity: true}})
                        }else{
                            this.$router.push({name: 'ViewActivitySurvey', params: {id: response.data.data.id, surveyId: response.data.data.attributes.activity_page_id, successfullyCreatedActivity: true}})
                        }
                    }
                }
            } catch (e) {
                if(e.response){
                    this.alertType = 'error'
                    if (e.response.status == 422 ){
                        this.alertMsg =  i18n.t('serverError')
                    } else if (e.response.status == 403){
                        this.$router.push({name: 'PageAccessDenied'})
                    } else {
                        this.alertMsg =  i18n.t('serverError')
                    }
                    this.$emit('proceedSubmit', this.alertMsg, this.alertType)
                }else{
                    this.alertType = 'error'
                    this.alertMsg =  i18n.t('serverError')     
                    this.$emit('proceedSubmit', this.alertMsg, this.alertType)  
                }
            }
            this.loading = false
        },

        async previewTemplate(template, templateType){
          this.loadingTemplate = true
          this.preview = true
          this.selectedTemplate = template
          this.selectedTemplate.type = templateType
          this.activityContainsScorm = false

          if(this.selectedTemplate.type == 'custom'){
            const response = await getSpacesTemplateSharedWith(this.selectedTemplate.id)
            this.spacesTemplateSharedWith = response.data.data
          }

          this.resetScroll()

          this.$nextTick(() => {
            if(this.selectedTemplate.feed_items.length > 0 && this.selectedTemplate.type == 'custom'){
              for(let i = 0; i < this.selectedTemplate.feed_items.length; i++){
                if(this.selectedTemplate.feed_items[i].entity_type == 'scorm'){
                  this.activityContainsScorm = true
                }
              }
            }
          })

          this.loadingTemplate = false
        },
        closeDialog() {
          if(!this.loading){
            this.$emit('close')
          }
        },
        async deleteTemplate(id){
          try{
              const response = await deleteTemplate(id)
              if(response.status == 204) {
                this.alertValue = true
                  this.alertType = 'success'
                  this.alertMsg =  i18n.t('deleteTemplateMsg')
                  this.dialogVisibleDeleteTemplate = false
                  this.preview = false
                  await this.fetchTemplates()
                  
              }
          } catch (e) {
              if(e.response){
                  this.alertType = 'error'
                  this.alertMsg =  i18n.t('serverError')
                  this.$emit('proceedSubmit', this.alertMsg, this.alertType)  
              }
          }
        },
        resetScroll(){
          this.$refs.content.scrollTop = 0
        },
        async showSuccessMsg(){
          this.dialogVisibleShareTemplate = false
          this.alertValue = true
          this.alertType = 'success'
          this.alertMsg =  i18n.t('shareTemplateSuccess')
          const response = await getSpacesTemplateSharedWith(this.selectedTemplate.id)
          this.spacesTemplateSharedWith = response.data.data
        }
    }
}
</script>
